import React, { useEffect, useState } from "react";
import {
    Bell,
    Mail,
    ShieldAlert,
    CalendarCheck,
    Users,
    CreditCard,
    Lock,
    Smartphone,
    MonitorCheck,
    Save,
} from "lucide-react";
import "../styles/NotificationPreferences.css";
import api from "../utils/api";
import { useNavigate } from "react-router-dom";
import ToggleSwitch from "../components/shared/ToggleSwitch";

const NotificationPreferences = () => {
    const [prefs, setPrefs] = useState({
        emailNotifications: true,
        inAppNotifications: true,
        parentMessages: true,
        complianceAlerts: true,
        reminders: true,
        newEnrollments: true,
        securityAlerts: true,
        attendanceAlerts: true,
        billingAlerts: false,
        criticalOnly: false,
    });

    const toggle = (key) => {
        setPrefs((prev) => ({
            ...prev,
            [key]: !prev[key],
        }));
    };

    const PreferenceRow = ({ icon, title, description, prefKey }) => (
        <div className="preference-row">
            <div className="preference-icon">{icon}</div>

            <div className="preference-copy">
                <h3>{title}</h3>
                <p>{description}</p>
            </div>

            <ToggleSwitch
                checked={prefs[prefKey]}
                onChange={(value) =>
                    setPrefs((prev) => ({
                        ...prev,
                        [prefKey]: value,
                    }))
                }
            />
        </div>
    );

    useEffect(() => {
        const loadPrefs = async () => {
            try {
                const res = await api.get("/api/notification-preferences");
                setPrefs(res.data);
            } catch (err) {
                console.error("Failed to load notification preferences:", err);
            }
        };

        loadPrefs();
    }, []);

    const [saved, setSaved] = useState(false);

    const navigate = useNavigate();

    const handleSavePreferences = async () => {
        try {
            const res = await api.patch("/api/notification-preferences", prefs);
            setPrefs(res.data);
            setSaved(true);
            setTimeout(() => setSaved(false), 2500);
        } catch (err) {
            console.error("Save preferences error:", err);
        }
    };

    const handleApplyAdminRecommended = async () => {


        try {
            const res = await api.patch(
                "/api/notification-preferences/admin-recommended"
            );

            setPrefs(res.data.userPreferences);

            setSaved("Admin settings applied");

            setTimeout(() => setSaved(false), 2500);
        } catch (err) {
            console.error("Apply admin recommended error:", err);
        }
    };


    return (
        <div className="notification-preferences-page">
            <div className="preferences-hero">
                <div>
                    <h1>Notification Preferences</h1>
                    <p>
                        Control how Kuddlecore keeps you informed about messages,
                        safety, compliance, and daily center activity.
                    </p>
                </div>

                <button
                    type="button"
                    className="back-btn"
                    onClick={() => navigate("/notifications")}
                >
                    🔔 Back to Notifications
                </button>
            </div>

            <div className="smart-note">
                <ShieldAlert size={20} />

                <span className="smart-note-text">
                    Recommended for admins: keep safety, compliance,
                    pickup, and security alerts enabled.
                </span>

                <button
                    type="button"
                    className="admin-recommended-btn"
                    onClick={handleApplyAdminRecommended}
                >
                    Apply Admin Recommended
                </button>
            </div>

            <section className="preferences-section">
                <h2>Delivery Methods</h2>

                <PreferenceRow
                    icon={<Mail size={20} />}
                    title="Email Notifications"
                    description="Receive important updates by email."
                    prefKey="emailNotifications"
                />

                <PreferenceRow
                    icon={<MonitorCheck size={20} />}
                    title="In-App Notifications"
                    description="Show alerts inside Kuddlecore."
                    prefKey="inAppNotifications"
                />

                <PreferenceRow
                    icon={<Smartphone size={20} />}
                    title="Critical Alerts Only"
                    description="Only receive high-priority safety and compliance notifications."
                    prefKey="criticalOnly"
                />
            </section>

            <section className="preferences-section">
                <h2>Notification Types</h2>

                <PreferenceRow
                    icon={<Mail size={20} />}
                    title="Parent Messages"
                    description="New parent and staff messages."
                    prefKey="parentMessages"
                />

                <PreferenceRow
                    icon={<ShieldAlert size={20} />}
                    title="Compliance Alerts"
                    description="Missing forms, expiring documents, and review items."
                    prefKey="complianceAlerts"
                />

                <PreferenceRow
                    icon={<CalendarCheck size={20} />}
                    title="Reminders"
                    description="Staff tasks, meetings, drills, and daily follow-ups."
                    prefKey="reminders"
                />

                <PreferenceRow
                    icon={<Users size={20} />}
                    title="New Enrollments"
                    description="New applications and student registration updates."
                    prefKey="newEnrollments"
                />

                <PreferenceRow
                    icon={<Lock size={20} />}
                    title="Security Alerts"
                    description="Pickup activity, device logins, password changes, and role updates."
                    prefKey="securityAlerts"
                />

                <PreferenceRow
                    icon={<Bell size={20} />}
                    title="Attendance Alerts"
                    description="Late check-ins, absences, no checkout, and ratio concerns."
                    prefKey="attendanceAlerts"
                />

                <PreferenceRow
                    icon={<CreditCard size={20} />}
                    title="Billing Alerts"
                    description="Unpaid balances, failed payments, and billing reminders."
                    prefKey="billingAlerts"
                />
            </section>

            <div className="preferences-actions">
                {saved && (
                    <span className="save-confirmation">
                        {saved === true ? "Preferences saved successfully." : saved}
                    </span>
                )}

                <button
                    type="button"
                    className="save-preferences-btn"
                    onClick={handleSavePreferences}
                >
                    <Save size={18} />
                    Save Preferences
                </button>
            </div>
        </div >
    );
}




export default NotificationPreferences;